import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';
import { leaderboardService } from '../../services/leaderboard.service';
import { getImageUrl } from '../../utils/imageHelper';

interface LeaderboardRecord {
  rank: number;
  student_id: number;
  student_name: string;
  student_avatar?: string;
  completion_time_ms: number;
  score?: number;
  attempt_count: number;
}

interface LeaderboardModalProps {
  setId: number;
  gameType: 'quiz' | 'matching';
  isOpen: boolean;
  onClose: () => void;
  title: string;
  totalCards?: number; // Total number of questions in the set
}

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 20px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;

  @media (max-width: 768px) {
    border-radius: 16px;
    max-height: 90vh;
  }

  @media (max-width: 480px) {
    width: 95%;
    max-height: 85vh;
    border-radius: 12px;
  }
`;

const Header = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 20px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: rotate(90deg);
  }

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;

  @media (max-width: 768px) {
    padding: 12px;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;

    &:hover {
      background: #555;
    }
  }
`;

const LeaderboardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 768px) {
    gap: 10px;
  }

  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const LeaderboardItem = styled.div<{ $rank: number }>`
  background: ${props => {
    if (props.$rank === 1) return 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)';
    if (props.$rank === 2) return 'linear-gradient(135deg, #c0c0c0 0%, #e8e8e8 100%)';
    if (props.$rank === 3) return 'linear-gradient(135deg, #cd7f32 0%, #e5b87a 100%)';
    return 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)';
  }};
  border-radius: 12px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;

  &:hover {
    transform: translateX(4px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    padding: 10px 12px;
    gap: 10px;
    border-radius: 10px;
  }

  @media (max-width: 480px) {
    padding: 8px 10px;
    gap: 8px;
    border-radius: 8px;
  }
`;

const RankBadge = styled.div<{ $rank: number }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => {
    if (props.$rank === 1) return '#ffc107';
    if (props.$rank === 2) return '#c0c0c0';
    if (props.$rank === 3) return '#cd7f32';
    return '#e0e0e0';
  }};
  color: ${props => props.$rank <= 3 ? 'white' : '#333'};
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 16px;
  }

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    font-size: 14px;
  }
`;

const AvatarImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 2px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
  }

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
  }
`;

const AvatarPlaceholder = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  flex-shrink: 0;
  border: 2px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
  }

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
  }
`;

const StudentInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StudentName = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 14px;

  @media (max-width: 768px) {
    font-size: 13px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const StudentTime = styled.div`
  font-size: 13px;
  color: #666;
  font-family: 'Courier New', monospace;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 12px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
  }
`;

const StudentScore = styled.div`
  font-size: 12px;
  color: #28a745;
  font-weight: 600;
  margin-top: 2px;
  display: flex;
  gap: 15px;
  justify-content: flex-start;

  @media (max-width: 768px) {
    font-size: 11px;
    gap: 12px;
  }

  @media (max-width: 480px) {
    font-size: 10px;
    gap: 10px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #999;

  div {
    font-size: 48px;
    margin-bottom: 16px;
  }

  p {
    font-size: 14px;
    margin: 0;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666;

  font-size: 16px;
`;

const formatTime = (milliseconds: number): string => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const ms = Math.floor((milliseconds % 1000) / 10);

  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
};

const LeaderboardModal: React.FC<LeaderboardModalProps> = ({ setId, gameType, isOpen, onClose, title, totalCards = 0 }) => {
  const [leaderboard, setLeaderboard] = useState<LeaderboardRecord[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadLeaderboard();
    }
  }, [isOpen, setId, gameType]);

  const loadLeaderboard = async () => {
    try {
      setLoading(true);
      console.log(`📡 Fetching leaderboard for setId=${setId}, gameType=${gameType}`);
      const response = await leaderboardService.getLeaderboard(setId, gameType);
      console.log('📥 Full API response:', response);
      console.log('📊 Response data:', response.data);
      if (response.data && response.data.length > 0) {
        console.log('📋 First record structure:', response.data[0]);
        console.log('🔍 Attempt count values:', response.data.map((r: any) => ({ name: r.student_name, attempt_count: r.attempt_count })));
      }
      if (response.success) {
        console.log('✅ Leaderboard data:', response.data);
        setLeaderboard(response.data || []);
      } else {
        console.error('❌ Leaderboard request failed:', response);
        setLeaderboard([]);
      }
    } catch (error) {
      console.error('❌ Error loading leaderboard:', error);
      setLeaderboard([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay $isOpen={isOpen} onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>{title}</Title>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </Header>

        <Content>
          {loading ? (
            <LoadingState>⏳ Đang tải xếp hạng...</LoadingState>
          ) : leaderboard.length === 0 ? (
            <EmptyState>
              <div>📋</div>
              <p>Chưa có ai chơi trò này</p>
            </EmptyState>
          ) : (
            <LeaderboardList>
              {leaderboard.map((record) => (
                <LeaderboardItem key={`${record.rank}-${record.student_id}`} $rank={record.rank}>
                  <RankBadge $rank={record.rank}>
                    {record.rank === 1 && '🏆'}
                    {record.rank === 2 && '🥈'}
                    {record.rank === 3 && '🥉'}
                    {record.rank > 3 && record.rank}
                  </RankBadge>

                  {record.student_avatar ? (
                    <AvatarImage src={getImageUrl(record.student_avatar)} alt={record.student_name} />
                  ) : (
                    <AvatarPlaceholder>{record.student_name.charAt(0).toUpperCase()}</AvatarPlaceholder>
                  )}

                  <StudentInfo>
                    <StudentName>{record.student_name}</StudentName>
                    {gameType === 'quiz' && record.score !== undefined && (
                      <StudentScore>
                        <span>✓ {totalCards > 0 ? Math.round((record.score / totalCards) * 100) : record.score}%</span>
                        <span>⏱️ {formatTime(record.completion_time_ms)}</span>
                        <span>{record.attempt_count} lần chơi</span>
                      </StudentScore>
                    )}
                    {gameType === 'matching' && (
                      <StudentScore>
                        <span>⏱️ {formatTime(record.completion_time_ms)}</span>
                        <span>{record.attempt_count} lần chơi</span>
                      </StudentScore>
                    )}
                  </StudentInfo>
                </LeaderboardItem>
              ))}
            </LeaderboardList>
          )}
        </Content>
      </ModalContainer>
    </Overlay>
  );
};

export default LeaderboardModal;
